// @wc-ignore-file
// (Pure data: the patterns and the replacement token are matched and emitted
// verbatim — a translated copy would stop matching, and would ship a
// translated placeholder into Sentry.)

/**
 * CR-111 stage 4 — last-resort scrubbing of Kairo API keys before anything
 * leaves the browser for Sentry.
 *
 * WHY THIS EXISTS. `main.tsx` runs Sentry with `enableLogs` and
 * `consoleLoggingIntegration({ levels: ["log", "warn", "error"] })`, so EVERY
 * `console.*` call anywhere in the app — ours or a dependency's — is forwarded
 * to a third party, with `sendDefaultPii: true`. Agent keys (`kairo_ak_`) and
 * personal keys (`kairo_sk_`) are bearer credentials for the MCP: one of them
 * in one log line is a live credential in someone else's system.
 *
 * Nothing logs a key today — the agent-key UI makes no console calls at all,
 * and the plaintext never enters the query cache. This is the guarantee that
 * survives the next person who adds a `console.log` while debugging, or the
 * next dependency that echoes a request body.
 *
 * It is a net, not a strategy. The strategy is not to put secrets anywhere
 * they could be logged in the first place.
 */

/** `kairo_sk_…` / `kairo_ak_…` followed by at least 8 hex characters. */
const KAIRO_KEY_PATTERN = /kairo_(sk|ak)_[0-9a-f]{8,}/gi;

const REDACTED = "kairo_$1_[redacted]";

/** How deep to walk before giving up; Sentry events are shallow in practice. */
const MAX_DEPTH = 8;

export function redactSecretsInString(value: string): string {
  return value.replace(KAIRO_KEY_PATTERN, REDACTED);
}

/**
 * Walks a Sentry event (or any plain structure) and redacts key-shaped
 * substrings, including inside object KEYS. Anything that is not a string,
 * array or plain object is returned untouched — this must never throw or
 * mangle an event, because the alternative to a scrubbed report is no report.
 */
export function redactSecrets<T>(value: T, depth = 0): T {
  if (typeof value === "string") {
    return redactSecretsInString(value) as T;
  }
  if (depth >= MAX_DEPTH || value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((item) => redactSecrets(item, depth + 1)) as T;
  }
  // Only plain objects: a Date, Error, Map or class instance is left alone
  // rather than silently rebuilt as a bare object.
  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    return value;
  }
  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    result[redactSecretsInString(key)] = redactSecrets(item, depth + 1);
  }
  return result as T;
}
